import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../../styles/landing/VideoBackground.css';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { MEDIA_URL } from '../../config/constants';

const VideoBackground = () => {
  const { consentGiven, resetConsent } = useCookieConsent();

  // Temporary: Add reset function to window for testing (remove in production)
  if (typeof window !== 'undefined') {
    window.resetCookieConsent = resetConsent;
  }

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videosReady, setVideosReady] = useState(false);
  const videoRefs = useRef([]);

  // Video sources - using optimized versions (~2MB instead of 40MB)
  const videos = useMemo(() => [
    {
      src: `${MEDIA_URL}/videos/beach-campervan-trip-home-optimized.mp4`,
      poster: `${MEDIA_URL}/images/blog/blog-placeholder.webp`,
    },
    {
      src: `${MEDIA_URL}/videos/gale-beach-campervan-trip-home-optimized.mp4`,
      poster: `${MEDIA_URL}/images/blog/blog-placeholder.webp`,
    },
  ], []);

  // Initialize video refs array
  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, videos.length);
  }, [videos.length]);

  // Preload all videos on mount
  useEffect(() => {
    let loadedCount = 0;
    const totalVideos = videos.length;

    const handleCanPlayThrough = () => {
      loadedCount++;
      if (loadedCount >= totalVideos) {
        setVideosReady(true);
      }
    };

    // Set up each video for preloading
    videoRefs.current.forEach((video) => {
      if (!video) return;

      // Mute for autoplay compatibility
      video.muted = true;
      video.volume = 0;
      video.defaultMuted = true;
      video.setAttribute('muted', '');

      // Listen for when video is fully buffered
      video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });

      // Start loading
      video.load();
    });

    return () => {
      videoRefs.current.forEach((video) => {
        if (video) {
          video.removeEventListener('canplaythrough', handleCanPlayThrough);
        }
      });
    };
  }, [videos]);

  // Play current video when ready or when consent given
  useEffect(() => {
    if (!videosReady) return;

    const currentVideo = videoRefs.current[currentVideoIndex];

    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index === currentVideoIndex) {
        // This is the active video - try to play it
        if (consentGiven && video.paused) {
          video.play().catch(() => {
            // Autoplay blocked by Firefox - add interaction listeners
            const startOnInteraction = () => {
              if (currentVideo && currentVideo.paused) {
                currentVideo.play().catch(() => {});
              }
              ['click', 'scroll', 'touchstart', 'keydown', 'pointerdown'].forEach(event => {
                document.removeEventListener(event, startOnInteraction);
              });
            };
            ['click', 'scroll', 'touchstart', 'keydown', 'pointerdown'].forEach(event => {
              document.addEventListener(event, startOnInteraction, { once: true });
            });
          });
        }
      } else {
        // Pause and reset other videos so they're ready for their turn
        if (!video.paused) {
          video.pause();
        }
        video.currentTime = 0;
      }
    });
  }, [currentVideoIndex, videosReady, consentGiven]);

  // Handle video end - switch to next video
  const handleVideoEnd = useCallback((index) => {
    if (index !== currentVideoIndex) return; // Ignore if not current video

    const nextIndex = (currentVideoIndex + 1) % videos.length;
    setCurrentVideoIndex(nextIndex);
  }, [currentVideoIndex, videos.length]);

  // Handle video error
  const handleVideoError = useCallback((e) => {
    console.warn('Video failed to load:', e.target.src);
  }, []);

  return (
    <div className="video-background-container">
      {/* All videos are always in DOM, only current one is visible */}
      {videos.map((video, index) => (
        <video
          key={video.src}
          ref={(el) => (videoRefs.current[index] = el)}
          className="hero-background-video"
          style={{
            opacity: index === currentVideoIndex ? 1 : 0,
            zIndex: index === currentVideoIndex ? 1 : 0,
          }}
          muted
          playsInline
          poster={video.poster}
          onEnded={() => handleVideoEnd(index)}
          onError={handleVideoError}
          preload="auto"
          disablePictureInPicture
          controls={false}
          {...(index === 0 && { fetchPriority: 'high' })}
        >
          <source src={video.src} type="video/mp4" />
        </video>
      ))}

      {/* Video overlay for better text readability */}
      <div className="video-overlay"></div>
    </div>
  );
};

export default VideoBackground;
